// ── Auto-translate for posts ─────────────────────────────────────
//
// Uses LibreTranslate (free, open source, no API key required).
// Falls back to MyMemory if LibreTranslate is unavailable.
// Language detection uses a simple heuristic based on character
// ranges — no external dependencies.
//
// Translations are cached in memory so repeated views don't re-fetch.

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

// LibreTranslate public instances (tried in order, fall back on failure)
const LIBRETRANSLATE_INSTANCES = [
  'https://libretranslate.com',
  'https://translate.terraprint.co',
  'https://trans.zillyhuhn.com',
];

// ── In-memory cache ─────────────────────────────────────────────

interface CacheEntry {
  translatedText: string;
  detectedLang: string;
  targetLang: string;
}

const translationCache = new Map<string, CacheEntry>();

function cacheKey(text: string, targetLang: string): string {
  // Use first 200 chars + length as key to avoid huge map keys
  return `${text.slice(0, 200)}::${text.length}::${targetLang}`;
}

// ── Language detection (lightweight heuristic) ──────────────────

/** Get the user's preferred language (e.g. "en", "es", "zh") */
export function getUserLanguage(): string {
  const lang = navigator.language || (navigator as any).userLanguage || 'en';
  return lang.split('-')[0].toLowerCase();
}

/**
 * Detect the likely language of a text using Unicode script analysis.
 * Returns an ISO 639-1 code or null if indeterminate.
 * This is a rough heuristic — not perfect, but fast and dependency-free.
 */
export function detectLanguage(text: string): string | null {
  if (!text || text.length < 10) return null;

  // Strip URLs, mentions, hashtags, emojis, and nostr: URIs for cleaner detection
  const cleaned = text
    .replace(/https?:\/\/\S+/g, '')
    .replace(/nostr:[a-z0-9]+/g, '')
    .replace(/@\S+/g, '')
    .replace(/#\S+/g, '')
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, '')
    .trim();

  if (cleaned.length < 8) return null;

  // Count characters in different Unicode script ranges
  let latin = 0, cjk = 0, cyrillic = 0, arabic = 0, devanagari = 0,
      hangul = 0, thai = 0, japanese = 0, total = 0;

  for (const char of cleaned) {
    const code = char.codePointAt(0)!;
    if (code < 0x20) continue; // control chars
    if (code >= 0x20 && code <= 0x7E) { latin++; total++; continue; } // Basic Latin
    if (code >= 0x00C0 && code <= 0x024F) { latin++; total++; continue; } // Latin Extended
    if (code >= 0x0400 && code <= 0x04FF) { cyrillic++; total++; continue; }
    if (code >= 0x0600 && code <= 0x06FF) { arabic++; total++; continue; }
    if (code >= 0x0900 && code <= 0x097F) { devanagari++; total++; continue; }
    if (code >= 0x0E00 && code <= 0x0E7F) { thai++; total++; continue; }
    if (code >= 0x3040 && code <= 0x309F) { japanese++; total++; continue; } // Hiragana
    if (code >= 0x30A0 && code <= 0x30FF) { japanese++; total++; continue; } // Katakana
    if (code >= 0xAC00 && code <= 0xD7AF) { hangul++; total++; continue; }
    if (code >= 0x4E00 && code <= 0x9FFF) { cjk++; total++; continue; } // CJK Unified
    if (code >= 0x3400 && code <= 0x4DBF) { cjk++; total++; continue; } // CJK Extension A
    total++;
  }

  if (total < 5) return null;

  // Determine dominant script
  const threshold = 0.3; // 30% of chars must be in a script
  if (cjk / total > threshold && japanese / total < 0.1) return 'zh';
  if (japanese / total > threshold || (cjk + japanese) / total > threshold && japanese > 0) return 'ja';
  if (hangul / total > threshold) return 'ko';
  if (cyrillic / total > threshold) return 'ru';
  if (arabic / total > threshold) return 'ar';
  if (devanagari / total > threshold) return 'hi';
  if (thai / total > threshold) return 'th';

  // For Latin scripts, we can't easily distinguish en/es/fr/pt/de etc.
  // from character analysis alone. Return null so the translation API
  // can auto-detect the source language.
  if (latin / total > 0.7) return null; // likely Latin-script language, let API detect

  return null;
}

/**
 * Check if a post has enough text content to be worth translating.
 * Always shows the translate button — let the user decide.
 * Only hides it for very short posts (under 20 chars) or empty content.
 */
export function mightNeedTranslation(text: string): boolean {
  if (!text) return false;
  // Strip URLs and nostr URIs to check actual text length
  const stripped = text
    .replace(/https?:\/\/\S+/g, '')
    .replace(/nostr:[a-z0-9]+/g, '')
    .trim();
  return stripped.length >= 20;
}

// ── Translation API ─────────────────────────────────────────────

export interface TranslationResult {
  translatedText: string;
  detectedLang: string;
  targetLang: string;
}

/**
 * Prepare text for translation: strip URLs/nostr URIs and replace with
 * placeholders so they don't get mangled by the translation engine.
 */
function prepareText(text: string): { cleanText: string; placeholders: { placeholder: string; original: string }[] } {
  const placeholders: { placeholder: string; original: string }[] = [];
  const cleanText = text.replace(/(https?:\/\/\S+|nostr:[a-z0-9]+)/g, (match) => {
    const placeholder = `__URL${placeholders.length}__`;
    placeholders.push({ placeholder, original: match });
    return placeholder;
  });
  return { cleanText, placeholders };
}

/** Restore URL placeholders in translated text */
function restoreUrls(translated: string, placeholders: { placeholder: string; original: string }[]): string {
  let result = translated;
  for (const { placeholder, original } of placeholders) {
    result = result.replace(placeholder, original);
  }
  return result;
}

/**
 * Try translating via LibreTranslate. Tries multiple public instances.
 */
async function translateViaLibreTranslate(
  cleanText: string,
  target: string,
  source: string,
): Promise<{ translatedText: string; detectedLang: string } | null> {
  for (const instance of LIBRETRANSLATE_INSTANCES) {
    try {
      const apiUrl = `${instance}/translate`;
      const body = JSON.stringify({
        q: cleanText,
        source: source === 'auto' ? 'auto' : source,
        target,
        format: 'text',
      });

      const res = await fetch(CORS_PROXY + encodeURIComponent(apiUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });

      if (!res.ok) continue;

      const data = await res.json();
      if (data.translatedText) {
        return {
          translatedText: data.translatedText,
          detectedLang: data.detectedLanguage?.language || source,
        };
      }
    } catch {
      // Try next instance
      continue;
    }
  }
  return null;
}

/**
 * Fallback: translate via MyMemory API (free, no key, lower quality).
 */
async function translateViaMyMemory(
  cleanText: string,
  target: string,
  source: string,
): Promise<{ translatedText: string; detectedLang: string }> {
  const langPair = `${source === 'auto' ? 'autodetect' : source}|${target}`;
  const apiUrl = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(cleanText)}&langpair=${encodeURIComponent(langPair)}`;

  const res = await fetch(CORS_PROXY + encodeURIComponent(apiUrl));
  if (!res.ok) throw new Error(`Translation failed: ${res.status}`);

  const data = await res.json();
  if (!data.responseData?.translatedText) {
    throw new Error('No translation returned');
  }

  const detectedLang = data.responseData?.detectedLanguage || source;
  return {
    translatedText: data.responseData.translatedText,
    detectedLang: typeof detectedLang === 'string' ? detectedLang.split('-')[0] : source,
  };
}

/**
 * Translate text. Tries LibreTranslate first, falls back to MyMemory.
 * Auto-detects source language.
 */
export async function translateText(
  text: string,
  targetLang?: string,
  sourceLang?: string,
): Promise<TranslationResult> {
  const target = targetLang || getUserLanguage();

  // Check cache first
  const key = cacheKey(text, target);
  const cached = translationCache.get(key);
  if (cached) return cached;

  // Truncate very long texts
  const truncated = text.length > 4500 ? text.slice(0, 4500) + '...' : text;

  const { cleanText, placeholders } = prepareText(truncated);
  const source = sourceLang || 'auto';

  // Try LibreTranslate first (better quality), fall back to MyMemory
  let raw = await translateViaLibreTranslate(cleanText, target, source);
  if (!raw) {
    raw = await translateViaMyMemory(cleanText, target, source);
  }

  const translatedText = restoreUrls(raw.translatedText, placeholders);

  const result: TranslationResult = {
    translatedText,
    detectedLang: raw.detectedLang.split('-')[0],
    targetLang: target,
  };

  // Cache the result
  translationCache.set(key, result);

  return result;
}

// ── Language names for display ──────────────────────────────────

const LANGUAGE_NAMES: Record<string, string> = {
  af: 'Afrikaans', ar: 'Arabic', bg: 'Bulgarian', bn: 'Bengali',
  ca: 'Catalan', cs: 'Czech', da: 'Danish', de: 'German',
  el: 'Greek', en: 'English', es: 'Spanish', et: 'Estonian',
  fa: 'Persian', fi: 'Finnish', fr: 'French', gu: 'Gujarati',
  he: 'Hebrew', hi: 'Hindi', hr: 'Croatian', hu: 'Hungarian',
  id: 'Indonesian', it: 'Italian', ja: 'Japanese', kn: 'Kannada',
  ko: 'Korean', lt: 'Lithuanian', lv: 'Latvian', mk: 'Macedonian',
  ml: 'Malayalam', mr: 'Marathi', ms: 'Malay', nl: 'Dutch',
  no: 'Norwegian', pl: 'Polish', pt: 'Portuguese', ro: 'Romanian',
  ru: 'Russian', sk: 'Slovak', sl: 'Slovenian', sq: 'Albanian',
  sr: 'Serbian', sv: 'Swedish', sw: 'Swahili', ta: 'Tamil',
  te: 'Telugu', th: 'Thai', tl: 'Filipino', tr: 'Turkish',
  uk: 'Ukrainian', ur: 'Urdu', vi: 'Vietnamese', zh: 'Chinese',
};

export function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code.toLowerCase()] || code.toUpperCase();
}

